import { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { Typography, Button, Stack } from "@altimateai/lego";
import { DateRange } from "@/components/dateRange";
import dayjs from "dayjs";
import { BackButton } from "@/components/button";
import { useAiGatewayEndpointDetail, useAiGatewayEndpointTrend } from "../../hooks";
import { useAppState } from "@/modules/app/useAppContext";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import { AiGatewayEndpointTrendChart } from "./AiGatewayEndpointTrendChart";
import { FlexListSummary } from "@/components/listSummary";
import { KeyInfoGrid } from "@/components/keyInfo";
import { formatCost } from "@/helpers/utils";
import { formatNumber } from "@/uiCore/components/formatters/format";
import { useBreadcrumb } from "@/components/breadcrumbs";

const AiGatewayEndpointDetail = (): JSX.Element => {
  const { endpointId: endpointName } = useParams<{ endpointId: string }>();
  useBreadcrumb(endpointName ? decodeURIComponent(endpointName) : "AI Gateway Endpoint", {
    defaultParents: [{ label: "AI/ML", pathname: "/ai-ml" }],
  });
  const { startDate, endDate, onUpdate } = useDateFromSearchParams(
    dayjs().subtract(28, "day").toDate(),
    dayjs().toDate()
  );
  const { currency } = useAppState();

  const formattedStartDate = dayjs(startDate).format("YYYY-MM-DD");
  const formattedEndDate = dayjs(endDate).format("YYYY-MM-DD");

  const {
    data: detail,
    isLoading: detailLoading,
    isError: detailError,
  } = useAiGatewayEndpointDetail(
    {
      endpoint_name: endpointName || "",
      start_date: formattedStartDate,
      end_date: formattedEndDate,
    },
    Boolean(endpointName)
  );

  const { data: trendData, isLoading: trendLoading } = useAiGatewayEndpointTrend(
    {
      endpoint_name: endpointName || "",
      start_date: formattedStartDate,
      end_date: formattedEndDate,
    },
    Boolean(endpointName)
  );

  const summary = useMemo(
    () => [
      {
        label: "Total Cost",
        value: detail ? formatCost(detail.total_cost_usd, currency) : "-",
      },
      { label: "Total DBUs", value: detail ? formatNumber(detail.total_dbus) : "-" },
      { label: "Days Active", value: detail ? formatNumber(detail.days_active) : "-" },
    ],
    [detail, currency]
  );

  const keyInfoItems = useMemo(() => {
    if (!detail) return [];
    return [
      { label: "Status", value: "Active" },
      { label: "Type", value: "AI Gateway" },
      { label: "Workspace", value: detail.workspace_id },
      { label: "Endpoint ID", value: detail.endpoint_id },
      { label: "First Seen", value: detail.first_seen },
      { label: "Last Activity", value: detail.last_activity },
    ];
  }, [detail]);

  if (!detailLoading && (detailError || !detail)) {
    return (
      <div className="al-p-4">
        <Stack
          direction="column"
          gap="md"
          align="center"
          justify="center"
          className="al-min-h-[400px]"
        >
          <Typography variant="h4" className="al-text-red-500">
            Error loading endpoint details
          </Typography>
          <Typography className="al-text-gray-500">
            Please try again later or contact support if the problem persists.
          </Typography>
          <Button asChild>
            <Link to="/ai-ml">Back to AI/ML Services</Link>
          </Button>
        </Stack>
      </div>
    );
  }

  return (
    <Stack className="al-p-4" direction="column" gap="md">
      <Stack direction="row" align="center" justify="between">
        <Stack direction="row" align="center" gap="sm">
          <BackButton defaultUrl="/ai-ml" />
          {detailLoading ? (
            <div className="al-h-8 al-w-64 al-rounded-md al-animate-pulse al-bg-gray-15" />
          ) : (
            <Typography variant="page-heading">{detail?.endpoint_name}</Typography>
          )}
        </Stack>
        <DateRange
          startDate={startDate}
          endDate={endDate}
          onDateRangeSelect={onUpdate}
          id="endpoint-detail-date-range"
          maxDate={new Date()}
        />
      </Stack>

      <KeyInfoGrid
        title="Key Information"
        ariaLabel="Entity metadata"
        items={keyInfoItems}
        isLoading={detailLoading}
      />

      <FlexListSummary summary={summary} isLoading={detailLoading} skeletonCount={3} />

      <AiGatewayEndpointTrendChart data={trendData ?? []} isLoading={trendLoading} />
    </Stack>
  );
};

export default AiGatewayEndpointDetail;
